import React, { useEffect, useState } from "react";
import "./ApplyResignationForm.css";
import apiClient from "../utils/apiClient";
import SessionHandler from "../components/SessionHandler";

const NOTICE_PERIOD_DAYS = 60;

const ApplyResignation = () => {
  const [formData, setFormData] = useState({
    resignation_date: "",
    last_working_date: "",
    reason: "",
  });

  const [existingRequest, setExistingRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  /* ================= FETCH EXISTING REQUEST ================= */
  const fetchMyResignation = async () => {
    try {
      const data = await apiClient("/resignation/my/");
      if (data && data.status) {
        setExistingRequest(data);
      } else {
        setExistingRequest(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchMyResignation();
  }, []);

  /* ================= HELPERS ================= */
  const calculateLastWorkingDate = (dateStr) => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + NOTICE_PERIOD_DAYS);
    return date.toISOString().split("T")[0];
  };

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Auto-calculate last working date
    if (name === "resignation_date") {
      if (!value) {
        setFormData({
          resignation_date: "",
          last_working_date: "",
          reason: "",
        });
        return;
      }

      const lwd = calculateLastWorkingDate(value);

      setFormData((prev) => ({
        ...prev,
        resignation_date: value,
        last_working_date: lwd,
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    if (!formData.resignation_date || !formData.reason) {
      setMessage("❌ All fields are required.");
      setDialogOpen(true);
      setLoading(false);
      return;
    }

    try {
      await apiClient("/resignation/", {
        method: "POST",
        body: JSON.stringify(formData),
      });

      setMessage("✅ Resignation request submitted successfully.");
      setDialogOpen(true);
      fetchMyResignation();
    } catch (err) {
      console.error(err);
      setMessage(
        err?.detail || "❌ Failed to submit resignation."
      );
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="resignation-form-container">
      <h2>Apply for Resignation</h2>

      {/* EXISTING REQUEST WARNING */}
      {existingRequest && existingRequest.status !== "Rejected" && (
        <>
          <div className="warning">
            You already have a resignation request. You cannot apply again unless
            it is rejected.
          </div>

          <div className="resignation-status">
            <h3>Your Resignation Status:</h3>
            <span
              className={`status-badge status-${existingRequest.status.toLowerCase()}`}
            >
              {existingRequest.status}
            </span>
          </div>
        </>
      )}

      {/* FORM */}
      {(!existingRequest || existingRequest.status === "Rejected") && (
        <form className="resignation-form" onSubmit={handleSubmit}>
          <div className="date-row">
            <div className="form-group">
              <label>Resignation Date</label>
              <input
                type="date"
                name="resignation_date"
                value={formData.resignation_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Last Working Date (Auto calculated)</label>
              <input
                type="date"
                name="last_working_date"
                value={formData.last_working_date}
                readOnly
              />
            </div>
          </div>

          <div className="form-group">
            <label>Reason</label>
            <textarea
              name="reason"
              placeholder="Enter reason for resignation"
              value={formData.reason}
              onChange={handleChange}
              required
            />
          </div>

          <button type="submit" disabled={loading}>
            {loading ? "Submitting..." : "Submit Resignation"}
          </button>
        </form>
      )}

      {/* DIALOG */}
      {dialogOpen && (
        <div className="dialog-overlay">
          <div className="dialog-box">
            <p>{message}</p>
            <button
              onClick={() => {
                setDialogOpen(false);
                window.location.reload();
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <SessionHandler />
    </div>
  );
};

export default ApplyResignation;
